import { Handle, Position, type NodeProps } from '@xyflow/react';
import { AlertTriangle, Sparkles } from 'lucide-react';
import { FrictionScore, NOTABLE_EXIT_RATE } from '../../../shared/ui/FrictionScore.js';
import './step-node.css';

export interface StepNodeData extends Record<string, unknown> {
  name: string;
  pagePath: string;
  position: number;
  sessionCount: number;
  dropOffRate: number;
  frictionScore: number;
  guidanceCount: number;
  proposedCount: number;
  isStale: boolean;
}

/**
 * One node of the lineage: a step reconstituted from measurement.
 *
 * Four handles, so an edge can attach on the side that matches the flow:
 * right → left inside a row, bottom → top when the journey wraps to the next.
 */
export function StepNode({ data, selected }: NodeProps & { data: StepNodeData }) {
  const notableExit = data.dropOffRate >= NOTABLE_EXIT_RATE;

  return (
    <div className={`node${selected ? ' node--selected' : ''}`}>
      <Handle id="in-left" type="target" position={Position.Left} className="node__handle" />
      <Handle id="in-top" type="target" position={Position.Top} className="node__handle" />

      <div className="node__head">
        <span className="node__index">{data.position}</span>
        <span className="node__name" title={data.name}>
          {data.name}
        </span>
        {data.isStale && (
          <span className="node__stale" title="Étape absente de la dernière analyse">
            <AlertTriangle size={12} />
          </span>
        )}
      </div>

      <div className="node__path" title={data.pagePath}>
        {data.pagePath}
      </div>

      <div className="node__metrics">
        <span className="node__metric">
          <strong>{data.sessionCount.toLocaleString('fr-FR')}</strong> usagers
        </span>
        <span className={`node__metric${notableExit ? ' node__metric--alert' : ''}`}>
          <strong>{Math.round(data.dropOffRate * 100)} %</strong> ont quitté
        </span>
      </div>

      <div
        className="node__friction"
        title="Score de friction : hésitations, erreurs et abandons sur la page"
      >
        <span className="node__friction-label">Friction</span>
        <FrictionScore score={data.frictionScore} />
      </div>

      {(data.guidanceCount > 0 || data.proposedCount > 0) && (
        <div className="node__guidance">
          {data.guidanceCount > 0 && (
            <span className="node__chip node__chip--live">{data.guidanceCount} en ligne</span>
          )}
          {data.proposedCount > 0 && (
            <span className="node__chip node__chip--proposed">
              <Sparkles size={11} /> {data.proposedCount} proposée{data.proposedCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}

      <Handle id="out-right" type="source" position={Position.Right} className="node__handle" />
      <Handle id="out-bottom" type="source" position={Position.Bottom} className="node__handle" />
    </div>
  );
}
